<template>
  <div class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-backdrop-blur-sm tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-4">
    <!-- Video Call Container -->
    <div class="tw-relative tw-w-full tw-max-w-4xl tw-h-[80vh] tw-max-h-[600px] tw-min-h-[400px] tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden tw-mx-4">
      <!-- Header with controls - Hidden -->
      <div class="tw-absolute tw-top-4 tw-right-4 tw-z-10 tw-flex tw-gap-2" style="display: none;">
        <a-button @click="minimizeCall" intent="secondary" size="sm">
          <icon icon="solar:minimize-square-bold-duotone" />
        </a-button>
        <a-button @click="endCall" intent="danger" size="sm">
          <icon icon="solar:phone-bold-duotone" />
        </a-button>
      </div>
      <!-- Call Header -->
      <div class="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-z-10 tw-bg-black tw-text-white tw-px-4 tw-py-1 tw-flex tw-items-center tw-justify-between">
        <div class="tw-flex tw-items-center tw-gap-3">
          <div class="tw-w-6 tw-h-6 tw-rounded-full tw-overflow-hidden tw-flex-shrink-0">
            <img 
              v-if="otherParticipant?.profile_image" 
              :src="otherParticipant.profile_image" 
              :alt="otherParticipant.name"
              class="tw-w-full tw-h-full tw-object-cover"
            />
            <div v-else class="tw-w-full tw-h-full tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
              <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
            </div>
          </div>
          <div>
            <h3 class="tw-text-lg tw-font-semibold">{{ otherParticipant?.name || 'Video Call' }}</h3>
          </div>
        </div>
        <p class="tw-text-sm tw-opacity-75">{{ callDuration || 'Connecting...' }}</p>
        
        <!-- Call Controls - Hidden -->
        <div v-if="false" class="tw-flex tw-items-center tw-gap-2" style="display: none;">
          <!-- Minimize Button -->
          <button
            @click="minimizeCall"
            class="tw-p-2 tw-text-white hover:tw-bg-white hover:tw-bg-opacity-20 tw-rounded-lg tw-transition-colors"
            title="Minimize"
          >
            <icon icon="solar:minimize-bold-duotone" class="tw-text-lg" />
          </button>
          
          <!-- Call Controls -->
          <div class="tw-flex tw-items-center tw-gap-2">
            <!-- Camera Toggle -->
            <button
              @click="toggleCamera"
              class="tw-p-2 tw-text-white hover:tw-bg-white hover:tw-bg-opacity-20 tw-rounded-lg tw-transition-colors"
              title="Toggle Camera"
            >
              <icon icon="solar:videocamera-bold-duotone" class="tw-text-lg" />
            </button>
            
            <!-- Microphone Toggle -->
            <button
              @click="toggleMicrophone"
              class="tw-p-2 tw-text-white hover:tw-bg-white hover:tw-bg-opacity-20 tw-rounded-lg tw-transition-colors"
              title="Toggle Microphone"
            >
              <icon icon="solar:microphone-bold-duotone" class="tw-text-lg" />
            </button>
            
            <!-- Screen Share Toggle -->
            <button
              @click="toggleScreenshare"
              class="tw-p-2 tw-text-white hover:tw-bg-white hover:tw-bg-opacity-20 tw-rounded-lg tw-transition-colors"
              title="Share Screen"
            >
              <icon icon="solar:monitor-bold-duotone" class="tw-text-lg" />
            </button>
            
            <!-- End Call Button -->
            <button
              @click="endCall"
              class="tw-p-2 tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-rounded-lg tw-transition-colors tw-ml-2"
              title="End Call"
            >
              <icon icon="solar:phone-calling-bold-duotone" class="tw-text-lg tw-rotate-180" />
            </button>
          </div>
        </div>
      </div>

      <!-- Video Container -->
      <div class="tw-w-full tw-h-full tw-relative" ref="videoContainer">
        <!-- Loading State -->
        <div v-if="loading" class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-gray-100">
          <div class="tw-text-center">
            <div class="tw-w-16 tw-h-16 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4 tw-mx-auto">
              <icon icon="line-md:loading-twotone-loop" class="tw-text-2xl tw-text-primary-600" />
            </div>
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">{{ loadingMessage }}</h3>
            <p class="tw-text-sm tw-text-gray-600">Please wait while we connect you...</p>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-red-50">
          <div class="tw-text-center tw-max-w-md tw-px-4">
            <div class="tw-w-16 tw-h-16 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4 tw-mx-auto">
              <icon icon="solar:danger-bold-duotone" class="tw-text-2xl tw-text-red-500" />
            </div>
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Connection Failed</h3>
            <p class="tw-text-sm tw-text-gray-600 tw-mb-4">{{ error }}</p>
            <!-- Error State Buttons - Hidden -->
            <div class="tw-flex tw-gap-2 tw-justify-center" style="display: none;">
              <a-button @click="retryConnection" intent="primary" size="sm">
                Try Again
              </a-button>
              <a-button @click="endCall" intent="secondary" size="sm">
                Close
              </a-button>
            </div>
          </div>
        </div>

        <!-- Whereby Web Component -->
        <div v-if="roomUrl && !error" class="tw-relative tw-w-full tw-h-full">
          <whereby-embed
            :room="roomUrl"
            :displayName="currentUser?.name || 'Guest'"
            background="off"
            floatSelf="true"
            :userRole="userRole || 'participant'"
            :canEdit="userRole === 'host'"
            :canShare="userRole === 'host'"
            ref="wherebyEmbed"
            class="tw-w-full tw-h-full"
          />
          
          <!-- Loading overlay while embed initializes -->
          <div v-if="loading" class="tw-absolute tw-inset-0 tw-bg-white tw-bg-opacity-90 tw-flex tw-items-center tw-justify-center tw-z-10">
            <div class="tw-text-center">
              <div class="tw-w-16 tw-h-16 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4 tw-mx-auto">
                <icon icon="solar:videocamera-bold-duotone" class="tw-text-2xl tw-text-primary-600" />
              </div>
              <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Connecting to Video Call</h3>
              <p class="tw-text-sm tw-text-gray-600">{{ loadingMessage }}</p>
            </div>
          </div>
        </div>
        
        <!-- Fallback Video Call Interface -->
        <div v-else-if="shouldShowFallback" class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-gray-100">
          <div class="tw-text-center tw-max-w-md tw-px-4">
            <div class="tw-w-16 tw-h-16 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4 tw-mx-auto">
              <icon icon="solar:videocamera-bold-duotone" class="tw-text-2xl tw-text-primary-600" />
            </div>
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Video Call Active</h3>
            <p class="tw-text-sm tw-text-gray-600 tw-mb-4">
              Connected to room: {{ roomId }}
            </p>
            <p class="tw-text-xs tw-text-gray-500">
              {{ fallbackMessage }}
            </p>
            <!-- Fallback Interface Button - Hidden -->
            <div class="tw-flex tw-gap-2 tw-justify-center tw-mt-4" style="display: none;">
              <a-button @click="endCall" intent="danger" size="sm">End Call</a-button>
            </div>
          </div>
        </div>
        
        <!-- Custom Controls (hidden since Whereby provides their own) -->
        <div v-if="false" class="tw-absolute tw-bottom-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-flex tw-gap-2 tw-z-10">
          <a-button @click="toggleCamera" intent="secondary" size="sm">
            <icon icon="solar:camera-bold-duotone" />
          </a-button>
          <a-button @click="toggleMicrophone" intent="secondary" size="sm">
            <icon icon="solar:microphone-bold-duotone" />
          </a-button>
          <a-button @click="toggleScreenshare" intent="secondary" size="sm">
            <icon icon="solar:monitor-bold-duotone" />
          </a-button>
          <a-button @click="endCall" intent="danger" size="sm">
            <icon icon="solar:phone-bold-duotone" />
          </a-button>
        </div>
        
        <!-- Debug Info (hidden in production) -->
        <div v-if="false" class="tw-absolute tw-top-20 tw-left-4 tw-bg-black tw-text-white tw-p-4 tw-rounded-lg tw-z-20 tw-text-xs">
          <div>🔍 Debug Info:</div>
          <div>Loading: {{ loading }}</div>
          <div>Error: {{ error }}</div>
          <div>Room URL: {{ roomUrl }}</div>
          <div>User Role: {{ userRole }}</div>
          <div>Whereby Element: {{ !!wherebyEmbed }}</div>
          <div>Embed Timed Out: {{ embedTimedOut }}</div>
          <div>Computed States:</div>
          <div>- Show Whereby: {{ shouldShowWherebyEmbed }}</div>
          <div>- Show Fallback: {{ shouldShowFallback }}</div>
          <div>- Fallback Message: {{ fallbackMessage }}</div>
          <div>Template State:</div>
          <div>- Loading: {{ loading }}</div>
          <div>- Error: {{ !!error }}</div>
          <div>- Room URL: {{ !!roomUrl }}</div>
          <div>- Whereby Ref: {{ !!wherebyEmbed }}</div>
          <div>- Embed Timed Out: {{ embedTimedOut }}</div>
          <div>- DOM Elements: {{ domElementCount }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useAuthStore } from '@/stores';

const props = defineProps({
  roomId: {
    type: String,
    required: true
  },
  conversationId: {
    type: String,
    required: true
  },
  otherParticipant: {
    type: Object,
    default: null
  },
  callType: {
    type: String,
    default: 'video'
  },
  roomUrl: {
    type: String,
    default: null
  },
  userRole: {
    type: String,
    default: 'participant'
  },
  callId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['call-ended', 'call-minimized', 'error']);

// Reactive data
const loading = ref(true);
const error = ref(null);
const roomUrl = ref(props.roomUrl);
const userRole = ref(props.userRole);
const callStartTime = ref(null);
const callDuration = ref(null);
const videoContainer = ref(null);
const wherebyEmbed = ref(null);
const loadingMessage = ref('Initializing video call...');
const embedTimedOut = ref(false);

// Get auth store
const authStore = useAuthStore();
const currentUser = computed(() => authStore.user);

// Call duration timer
let durationInterval = null;
let embedTimeout = null;
let workingCheckInterval = null;

/**
 * Initialize the video call
 */
const initializeCall = async () => {
  try {
    // Reset states for new call
    loading.value = true;
    error.value = null;
    embedTimedOut.value = false;
    loadingMessage.value = 'Setting up your video call...';
    
    // Clear any existing timeouts
    if (embedTimeout) {
      clearTimeout(embedTimeout);
      embedTimeout = null;
    }
    
    // Check if we have a roomUrl prop, if not, try to get it from the service
    if (!roomUrl.value) {
      loadingMessage.value = 'Getting room details...';
      console.log('🔍 No roomUrl provided, fetching from service with roomId:', props.roomId);
      
      try {
        // Get room details from backend video call system instead of Whereby
        const roomResponse = await fetch(`/api/video-calls/rooms/${props.roomId}`);
        if (roomResponse.ok) {
          const roomData = await roomResponse.json();
          // Use the new participant_room_url field
          roomUrl.value = roomData.data?.participant_room_url || roomData.data?.room_url;
          userRole.value = roomData.data?.user_role || 'participant'; // Extract user role
          console.log('✅ Room details fetched:', roomData);
          console.log('🔗 Using participant room URL:', roomUrl.value);
          console.log('🔍 URL source:', {
            participant_room_url: roomData.data?.participant_room_url,
            room_url: roomData.data?.room_url,
            final_url: roomUrl.value
          });
        } else {
          throw new Error('Failed to fetch room details');
        }
      } catch (fetchError) {
        console.warn('⚠️ Could not fetch room details, using fallback:', fetchError);
        // Fallback: try to construct a URL from the roomId using the correct domain format
        roomUrl.value = `https://the-foode-show-demo.whereby.com/foodshow${props.roomId}`;
        console.log('🔄 Using fallback URL:', roomUrl.value);
      }
    }
    
    if (!roomUrl.value) {
      throw new Error('No room URL available');
    }
    
    // Validate room URL format
    console.log('🔍 Validating room URL format:', roomUrl.value);
    
    // Check if the URL looks like a valid Whereby URL
    if (!roomUrl.value.includes('whereby.com') && !roomUrl.value.includes('localhost')) {
      console.warn('⚠️ Room URL does not appear to be a valid Whereby URL:', roomUrl.value);
      
      // Try to construct a valid Whereby URL from the roomId using the correct domain format
      const fallbackUrl = `https://the-foode-show-demo.whereby.com/foodshow${props.roomId}`;
      console.log('🔄 Using fallback URL:', fallbackUrl);
      roomUrl.value = fallbackUrl;
    }
    
    // Log the final URL being used
    console.log('🎯 FINAL ROOM URL FOR WHEREBY EMBED:', roomUrl.value);
    console.log('👤 USER ROLE FOR WHEREBY EMBED:', userRole.value);
    
    loadingMessage.value = 'Connecting to the room...';
    console.log('🔗 Connecting to room URL:', roomUrl.value);
    
    // The API now provides the participant_room_url directly, so we don't need to modify it
    // Just use it as is for the Whereby embed
    console.log('👤 Using participant room URL directly from API:', roomUrl.value);
    
    // Keep loading true - it will be set to false when the embed is ready
    // or when we have an error/timeout
    console.log('🔄 Room URL ready, waiting for Whereby embed to initialize...');
    
    // Setup event listeners after room URL is set
    nextTick(() => {
      console.log('🔄 Setting up Whereby listeners...');
      waitForWherebyEmbed();
    });
    
  } catch (err) {
    console.error('Error initializing video call:', err);
    error.value = err.message || 'Failed to initialize video call';
    loading.value = false;
    if (embedTimeout) clearTimeout(embedTimeout);
  }
};

/**
 * Wait for the Whereby embed element to be available in the DOM
 */
const waitForWherebyEmbed = () => {
  console.log('⏳ Waiting for Whereby embed element...');
  console.log('🔍 Current DOM state:', {
    roomUrl: roomUrl.value,
    loading: loading.value,
    error: error.value,
    embedTimedOut: embedTimedOut.value
  });
  
  let retryCount = 0;
  const maxRetries = 200; // 20 seconds maximum wait time for element to appear
  
  const checkElement = () => {
    // Check both the ref and try to find the element in the DOM
    const element = wherebyEmbed.value || document.querySelector('whereby-embed');
    
    console.log(`🔍 Check ${retryCount + 1}:`, {
      elementFound: !!element,
      elementType: element ? element.tagName : 'none',
      wherebyEmbedRef: !!wherebyEmbed.value,
      domElements: document.querySelectorAll('whereby-embed').length
    });
    
    if (element) {
      console.log('✅ Whereby embed element found, setting up listeners');
      // Update the ref if we found it via DOM query
      if (!wherebyEmbed.value) {
        wherebyEmbed.value = element;
        console.log('✅ Whereby embed ref updated');
      }
      
      // Start timeout for embed initialization (only after element is found)
      embedTimeout = setTimeout(() => {
        console.warn('⏰ Whereby embed initialization timeout, showing fallback interface');
        loading.value = false;
        embedTimedOut.value = true;
      }, 30000); // 30 seconds timeout for embed to fully initialize
      
      setupWherebyListeners();
    } else if (retryCount < maxRetries) {
      retryCount++;
      console.log(`⏳ Whereby embed element not ready yet, retrying... (${retryCount}/${maxRetries})`);
      // Retry after a short delay
      setTimeout(checkElement, 100);
    } else {
      console.warn('⚠️ Whereby embed element not found after maximum retries, showing fallback interface');
      loading.value = false;
      embedTimedOut.value = true;
    }
  };
  
  // Start checking
  checkElement();
};

/**
 * Setup Whereby event listeners
 */
const setupWherebyListeners = () => {
  console.log('🎧 Setting up Whereby event listeners...');
  console.log('🔍 Whereby embed element:', wherebyEmbed.value);
  
  if (!wherebyEmbed.value) {
    console.error('❌ Whereby embed element not found!');
    error.value = 'Video call interface not available';
    loading.value = false;
    return;
  }
  
  const element = wherebyEmbed.value;
  console.log('✅ Whereby embed element found:', element);
  
  // Check if the element has the necessary methods
  console.log('🔍 Whereby embed element properties:', {
    tagName: element.tagName,
    className: element.className,
    hasReady: typeof element.addEventListener === 'function',
    elementType: typeof element
  });
  
  // Verify the element is actually a Whereby embed
  if (element.tagName !== 'WHEREBY-EMBED') {
    console.warn('⚠️ Element is not a Whereby embed:', element.tagName);
    error.value = 'Invalid video call element';
    loading.value = false;
    return;
  }
  
  // Listen for room ready
  element.addEventListener('ready', () => {
    console.log('✅ Whereby room is ready');
    loading.value = false;
    embedTimedOut.value = false; // Clear timeout flag
    
    // Clear the timeout since the embed loaded successfully
    if (embedTimeout) {
      clearTimeout(embedTimeout);
      embedTimeout = null;
    }
    
    callStartTime.value = new Date();
    startDurationTimer();
  });
  
  // Listen for participant events
  element.addEventListener('participantupdate', (event) => {
    console.log('👥 Participant update:', event.detail);
  });
  
  // Listen for user leaving
  element.addEventListener('leave', () => {
    console.log('👋 User left the room');
    endCall();
  });
  
  // Listen for errors
  element.addEventListener('room_connection_status', (event) => {
    console.log('📡 Room connection status:', event.detail);
    if (event.detail.status === 'connection_failed') {
      error.value = 'Failed to connect to video call';
      loading.value = false;
    }
  });
  
  // Listen for room joining
  element.addEventListener('room_joined', (event) => {
    console.log('🚪 Room joined:', event.detail);
    loading.value = false;
    embedTimedOut.value = false;
    
    // Clear the timeout since we successfully joined
    if (embedTimeout) {
      clearTimeout(embedTimeout);
      embedTimeout = null;
    }
  });
  
  // Listen for room joining failure
  element.addEventListener('room_join_failed', (event) => {
    console.error('❌ Room join failed:', event.detail);
    error.value = 'Failed to join video call room';
    loading.value = false;
  });
  
  // Listen for any other errors
  element.addEventListener('error', (event) => {
    console.error('❌ Whereby embed general error:', event.detail);
    error.value = 'Video call connection error';
    loading.value = false;
  });
  
  // Set a fallback timeout in case the embed doesn't respond
  setTimeout(() => {
    if (loading.value) {
      console.warn('⚠️ Whereby embed not responding, checking status...');
      // Check if the element is still valid
      if (element && element.tagName === 'WHEREBY-EMBED') {
        console.log('✅ Whereby embed element still valid, continuing to wait...');
        
        // If the element is valid and we're still loading, check if it's actually working
        // by looking for any Whereby-specific properties or events
        if (element.roomUrl || element.room || element.connected) {
          console.log('✅ Whereby embed appears to be working, clearing timeout');
          loading.value = false;
          embedTimedOut.value = false;
          
          // Clear the timeout since the embed is working
          if (embedTimeout) {
            clearTimeout(embedTimeout);
            embedTimeout = null;
          }
        }
        
        // Also check for Whereby-specific DOM elements that indicate the embed is working
        const wherebyElements = document.querySelectorAll('[class*="whereby"], [id*="whereby"], [data-whereby]');
        if (wherebyElements.length > 0) {
          console.log('✅ Found Whereby-specific DOM elements, embed appears to be working');
          loading.value = false;
          embedTimedOut.value = false;
          
          // Clear the timeout since the embed is working
          if (embedTimeout) {
            clearTimeout(embedTimeout);
            embedTimeout = null;
          }
        }
      } else {
        console.error('❌ Whereby embed element lost, showing fallback');
        error.value = 'Video call element not responding';
        loading.value = false;
      }
    }
  }, 15000); // 15 second check
  
  console.log('🎧 Whereby event listeners set up successfully');
  
  // Add a periodic check to see if the embed is working
  workingCheckInterval = setInterval(() => {
    if (!loading.value || embedTimedOut.value) {
      // Stop checking if we're no longer loading or have timed out
      clearInterval(workingCheckInterval);
      workingCheckInterval = null;
      return;
    }
    
    console.log('🔍 Periodic check: Whereby embed status...');
    
    // Check if the element has any Whereby-specific content or is responding
    if (element && element.tagName === 'WHEREBY-EMBED') {
      // Check for any video elements or Whereby-specific content
      const videoElements = element.querySelectorAll('video, canvas, [class*="whereby"], [id*="whereby"]');
      if (videoElements.length > 0) {
        console.log('✅ Found video elements in Whereby embed, it appears to be working');
        loading.value = false;
        embedTimedOut.value = false;
        
        // Clear the timeout since the embed is working
        if (embedTimeout) {
          clearTimeout(embedTimeout);
          embedTimeout = null;
        }
        
        clearInterval(workingCheckInterval);
        workingCheckInterval = null;
      }
    }
  }, 2000); // Check every 2 seconds
};

/**
 * Start the call duration timer
 */
const startDurationTimer = () => {
  durationInterval = setInterval(() => {
    if (callStartTime.value) {
      const elapsed = Math.floor((new Date() - callStartTime.value) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      callDuration.value = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  }, 1000);
};

/**
 * End the video call
 * Now properly includes callId for API calls (preferred) with roomId as fallback
 */
const endCall = () => {
  if (durationInterval) {
    clearInterval(durationInterval);
  }
  
  const duration = callStartTime.value ? Math.floor((new Date() - callStartTime.value) / 1000) : 0;
  
  emit('call-ended', {
    duration,
    roomId: props.roomId,
    conversationId: props.conversationId,
    callId: props.callId, // Include callId for API calls
    callType: props.callType // Include callType for message metadata
  });
};

/**
 * Minimize the call
 * Includes callId and callType for consistency with endCall
 */
const minimizeCall = () => {
  emit('call-minimized', {
    roomId: props.roomId,
    conversationId: props.conversationId,
    callId: props.callId, // Include callId for consistency
    callType: props.callType // Include callType for consistency
  });
};

/**
 * Retry connection
 */
const retryConnection = () => {
  error.value = null;
  initializeCall();
};

/**
 * Add custom controls for the call
 */
const toggleCamera = () => {
  if (wherebyEmbed.value) {
    wherebyEmbed.value.toggleCamera();
  }
};

const toggleMicrophone = () => {
  if (wherebyEmbed.value) {
    wherebyEmbed.value.toggleMicrophone();
  }
};

const toggleScreenshare = () => {
  if (wherebyEmbed.value) {
    wherebyEmbed.value.toggleScreenshare();
  }
};

// Computed properties to determine which interface to show
const shouldShowWherebyEmbed = computed(() => {
  // This is now handled directly in the template with v-if="roomUrl && !error"
  return roomUrl.value && !error.value;
});

const shouldShowFallback = computed(() => {
  // Show fallback only if we have an error or have explicitly timed out
  // Don't show fallback while loading
  return roomUrl.value && !loading.value && (error.value || embedTimedOut.value);
});

const fallbackMessage = computed(() => {
  if (embedTimedOut.value) {
    return 'Whereby embed not available. Please check your connection.';
  }
  return 'Error loading video call interface. Please check your connection.';
});

// Safely get DOM element count
const domElementCount = computed(() => {
  if (typeof window === 'undefined' || !window.document) {
    return 'N/A (SSR)';
  }
  try {
    return document.querySelectorAll('whereby-embed').length;
  } catch (error) {
    return 'Error';
  }
});

// Watch for changes in the computed properties to debug rendering
watch(shouldShowWherebyEmbed, (newValue, oldValue) => {
  console.log('🔄 shouldShowWherebyEmbed changed:', { oldValue, newValue });
  console.log('🔍 Current states:', {
    roomUrl: roomUrl.value,
    loading: loading.value,
    error: error.value,
    embedTimedOut: embedTimedOut.value
  });
});

watch(shouldShowFallback, (newValue, oldValue) => {
  console.log('🔄 shouldShowFallback changed:', { oldValue, newValue });
});

// Lifecycle
onMounted(() => {
  // Check if Whereby embed is available
  console.log('🔍 Checking Whereby embed availability...');
  console.log('Window object:', typeof window !== 'undefined');
  console.log('WherebyEmbed global:', typeof window !== 'undefined' ? window.WherebyEmbed : 'N/A');
  console.log('Custom elements:', typeof window !== 'undefined' ? window.customElements : 'N/A');
  
  if (typeof window !== 'undefined') {
    // Check if the custom element is defined
    const isWherebyEmbedDefined = window.customElements && window.customElements.get('whereby-embed');
    console.log('Whereby embed custom element defined:', isWherebyEmbedDefined);
    
    if (!isWherebyEmbedDefined) {
      console.error('❌ Whereby embed custom element not defined!');
      error.value = 'Video call service not available - Whereby embed not loaded';
      loading.value = false;
      return;
    }
    
    // Check if the WherebyEmbed global is available
    if (!window.WherebyEmbed) {
      console.warn('⚠️ WherebyEmbed global not available, but custom element is defined');
    }
  }
  
  console.log('✅ Whereby embed available, initializing call...');
  initializeCall();
});

onUnmounted(() => {
  if (durationInterval) {
    clearInterval(durationInterval);
  }
  if (embedTimeout) {
    clearTimeout(embedTimeout);
  }
  if (workingCheckInterval) {
    clearInterval(workingCheckInterval);
  }
});
</script>

<style scoped>
/* Ensure full screen video call */
whereby-embed {
  width: 100% !important;
  height: 100% !important;
  border: none;
  border-radius: 0;
}
</style>
